import { useCallback, useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { getMyShiftsRange, type MyShiftRow } from '../../api/assignments';

const TZ = 'America/Lima';

/** Primer y último día del mes que se está mirando, en formato YYYY-MM-DD. */
function rangoDelMes(mes: string): { desde: string; hasta: string } {
  const [a, m] = mes.split('-').map(Number);
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, '0')}` };
}

const DIA_CORTO = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/** Día de la semana SIN construir un Date local: una fecha suelta no tiene zona horaria. */
function diaDeLaSemana(fechaISO: string): string {
  const [a, m, d] = fechaISO.split('-').map(Number);
  return DIA_CORTO[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
}

const hhmm = (t: string) => t.slice(0, 5);

/**
 * "Mi mes": los turnos programados del recurso, con lo que suman.
 *
 * Pedido del usuario: *"podría ir a un panel donde vea qué turnos tiene ya planificados en todo
 * el mes, como un resumen"*. El resto de la aplicación de campo responde "¿qué hago hoy?"; esta
 * pantalla responde "¿qué tengo este mes y cuánto es?", que es lo que la persona necesita para
 * organizarse y para saber si su pago cuadra.
 *
 * Es también el primer ladrillo del panel propio del recurso: hoy muestra turnos, mañana puede
 * mostrar su desempeño.
 */
export default function MiMes() {
  const [mes, setMes] = useState(() => formatInTimeZone(new Date(), TZ, 'yyyy-MM'));
  const [turnos, setTurnos] = useState<MyShiftRow[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { desde, hasta } = rangoDelMes(mes);
      setTurnos(await getMyShiftsRange(desde, hasta));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar tus turnos.');
    } finally {
      setCargando(false);
    }
  }, [mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  const hoy = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const porVenir = turnos.filter((t) => t.position.date >= hoy).length;

  /**
   * Lo que suman las jornadas del mes. Es una ESTIMACIÓN de lo programado, no lo que se va a
   * cobrar: el pago sale del cierre, que descuenta medios días y suma lo que se validó. Se dice
   * con esas palabras para que nadie la confunda con su liquidación.
   */
  const estimado = turnos.reduce((s, t) => s + Number(t.position.rate_amount ?? 0), 0);
  const conTarifa = turnos.some((t) => Number(t.position.rate_amount ?? 0) > 0);

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-xl font-semibold">Mi mes</h1>

      <label className="block">
        <span className="text-sm text-gray-700">Mes</span>
        <input
          type="month"
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          className="mt-1 block w-full rounded border px-3 py-2"
        />
      </label>

      {error && (
        <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {!cargando && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border bg-white p-3">
            <p className="text-2xl font-bold text-gray-900">{turnos.length}</p>
            <p className="text-xs text-gray-600">jornadas programadas</p>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <p className="text-2xl font-bold text-gray-900">{porVenir}</p>
            <p className="text-xs text-gray-600">todavía por trabajar</p>
          </div>
          {conTarifa && (
            <div className="col-span-2 rounded-xl border bg-white p-3">
              <p className="text-2xl font-bold text-gray-900">S/ {estimado.toFixed(2)}</p>
              <p className="text-xs text-gray-600">
                estimado de lo programado — el monto final sale del cierre, que descuenta medios
                días y solo paga lo validado
              </p>
            </div>
          )}
        </div>
      )}

      {cargando ? (
        <p className="text-sm text-gray-500">Cargando tus turnos…</p>
      ) : turnos.length === 0 ? (
        <p className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-600">
          No tienes turnos aprobados en este mes. Si crees que deberías tenerlos, avísale a tu
          coordinador: un turno solo aparece aquí una vez aprobado.
        </p>
      ) : (
        <ul className="space-y-2">
          {turnos.map((t) => {
            const pasado = t.position.date < hoy;
            return (
              <li
                key={t.id}
                className={`rounded-xl border p-3 ${pasado ? 'border-gray-200 bg-gray-50' : 'border-blue-200 bg-white'}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium text-gray-900">
                    {diaDeLaSemana(t.position.date)} {t.position.date.slice(8)}/{t.position.date.slice(5, 7)}
                    {t.position.date === hoy && (
                      <span className="ml-2 rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">hoy</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600">
                    {hhmm(t.position.start_time)}–{hhmm(t.position.end_time)}
                  </p>
                </div>
                <p className="truncate text-sm text-gray-700">{t.position.pdv?.name ?? 'PDV sin nombre'}</p>
                <p className="truncate text-xs text-gray-500">
                  {t.position.campaign
                    ? `${t.position.campaign.client} · ${t.position.campaign.brand}`
                    : 'Campaña sin datos'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
